import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../../api/axios';
import { toast } from 'react-toastify';
import AdminNav from '../../components/AdminNav';
import { CheckCircle, XCircle, Loader2, User, Gift } from 'lucide-react';

export default function PendingReferrals() {
  const navigate = useNavigate();
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [rejectId, setRejectId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => { load(); }, []);

  const load = async () => {
    try { const res = await adminAPI.getPendingReferrals(); setReferrals(res.data.data || []); }
    catch { toast.error('Failed to load referrals'); }
    finally { setLoading(false); }
  };

  const handleApprove = async (id) => {
    setActionLoading(id);
    try { await adminAPI.approveReferral(id); toast.success('Referral approved!'); setReferrals(referrals.filter(r => r.id !== id)); }
    catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
    finally { setActionLoading(null); }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) { toast.error('Provide a rejection reason'); return; }
    setActionLoading(rejectId);
    try {
      await adminAPI.rejectReferral(rejectId, rejectReason);
      toast.success('Referral rejected');
      setReferrals(referrals.filter(r => r.id !== rejectId));
      setRejectId(null); setRejectReason('');
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
    finally { setActionLoading(null); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-gray-50"><Loader2 className="h-10 w-10 animate-spin text-teal-600" /></div>;

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminNav title="Pending Referrals" subtitle={referrals.length + " to review"} />
      <div className="max-w-7xl mx-auto px-6 py-8">
        {referrals.length === 0 ? (
          <div className="text-center py-20">
            <Gift className="h-16 w-16 text-purple-200 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-500">No pending referrals</h3>
          </div>
        ) : (
          <div className="space-y-4">
            {referrals.map((r) => (
              <div key={r.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center shrink-0">
                      <User className="h-5 w-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="font-bold text-slate-800">{r.referrerName} → {r.referredName}</p>
                      <p className="text-sm text-gray-500">Referrer: {r.referrerPhone}</p>
                      <p className="text-sm text-gray-500">Referred: {r.referredPhone}</p>
                      {r.incentiveAmount && (
                        <p className="text-sm font-semibold text-green-700 mt-1">Incentive: ₹{r.incentiveAmount}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <button onClick={() => handleApprove(r.id)} disabled={actionLoading === r.id}
                      className="flex items-center gap-1.5 bg-green-600 text-white px-5 py-2.5 rounded-xl font-bold text-sm hover:bg-green-700 transition disabled:opacity-50">
                      {actionLoading === r.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />} Approve
                    </button>
                    <button onClick={() => setRejectId(r.id)} disabled={actionLoading === r.id}
                      className="flex items-center gap-1.5 bg-red-50 text-red-600 border border-red-200 px-5 py-2.5 rounded-xl font-bold text-sm hover:bg-red-100 transition disabled:opacity-50">
                      <XCircle className="h-4 w-4" /> Reject
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {rejectId && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
            <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-md w-full">
              <h3 className="font-bold text-slate-800 mb-4">Reject Referral</h3>
              <textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Reason for rejection..." rows={3}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-red-500 resize-none" />
              <div className="flex gap-3 mt-4">
                <button onClick={() => { setRejectId(null); setRejectReason(''); }}
                  className="flex-1 py-2.5 border border-gray-200 rounded-xl font-semibold text-sm text-gray-600 hover:bg-gray-50">Cancel</button>
                <button onClick={handleReject} disabled={actionLoading === rejectId}
                  className="flex-1 py-2.5 bg-red-600 text-white rounded-xl font-bold text-sm hover:bg-red-700 disabled:opacity-50">
                  {actionLoading === rejectId ? 'Rejecting...' : 'Confirm Reject'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}